import React, { FC, useEffect, useReducer } from 'react';

import { Box, HStack } from '@chakra-ui/react';
import camelcase from 'camelcase';
import { filter, includes, isEmpty, map, split } from 'lodash';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDeepCompareEffect, useLocation } from 'react-use';
import { customTheme } from 'theme';

import { severityOptions } from 'components/DataDisplay/Utils/renderSeverity';
import { Select } from 'components/index';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { selectFindingsOverview } from 'containers/Findings/FindingsOverview/selectors';
import { selectSharedState } from 'containers/SharedState/selectors';
import { useSearchParams } from 'hooks/useSearchParams';

export const FindingTableFilter: FC = () => {
  const { currentCategory } = useSelector(selectFindingsOverview);
  const { alertResourceTypes, aiParamExtraction } = useSelector(
    selectSharedState,
  );
  const { getAlertResourceTypeOptions } = useResourceType();
  const { updateValue } = useSearchParamFunctions();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useSearchParams();
  const key = 'findings_overview_';

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      resourceTypeOptions: [],
      resourceType: [],
      severity: [],
      cloud: {},
    },
  );

  //set parameters from localstorage to url
  useDeepCompareEffect(() => {
    if (!params?.redirect) {
      const storageKey = key + camelcase(currentCategory as string);
      const severity = localStorage.getItem(storageKey + '_severity');
      const resourceType = localStorage.getItem(storageKey + '_resource_type');

      let searchParams = '';
      const setValue = (f, v) => {
        searchParams = searchParams + (!!searchParams ? '&' : '') + f + '=' + v;
      };

      if (!!severity) setValue('severity', severity);
      if (!!resourceType) setValue('resourceType', resourceType);
      if (!!searchParams)
        navigate(
          location.pathname + '?tab=' + currentCategory + '&' + searchParams,
        );
    }
  }, [navigate, location.pathname, currentCategory]);

  useDeepCompareEffect(() => {
    if (!isEmpty(aiParamExtraction.data)) {
      const data = aiParamExtraction.data;
      const severity = !!data?.severity
        ? [data?.severity]
        : !!data?.severities
        ? split(data?.severities, ',')
        : undefined;
      const resourceType = !!data?.resource_type
        ? [data?.resource_type]
        : !!data?.resourceTypes
        ? split(data?.resourceTypes, ',')
        : undefined;
      let searchParams = '';
      const setValue = (f, v) => {
        searchParams = searchParams + (!!searchParams ? '&' : '') + f + '=' + v;
      };

      if (!!severity) setValue('severity', severity);
      if (!!resourceType) setValue('resourceType', resourceType);
      if (!!searchParams)
        navigate(
          location.pathname + '?tab=' + currentCategory + '&' + searchParams,
        );
    }
  }, [aiParamExtraction.data, currentCategory]);

  //set resource type options
  useEffect(() => {
    const options = getAlertResourceTypeOptions(alertResourceTypes.data);
    updateState({
      resourceTypeOptions: options,
    });
  }, [alertResourceTypes.data, getAlertResourceTypeOptions]);

  //set resource types
  useEffect(() => {
    const resourceTypes = !!params.resourceType
      ? split(params.resourceType, ',')
      : [];
    const selected =
      filter(state.resourceTypeOptions, o =>
        includes(resourceTypes, o.value),
      ) || [];
    updateState({
      resourceType: selected,
    });
  }, [params.resourceType, state.resourceTypeOptions]);

  //set severities
  useEffect(() => {
    const severity = !!params.severity ? split(params.severity, ',') : [];
    updateState({
      severity:
        filter(severityOptions, o =>
          includes(severity, o.value?.toUpperCase()),
        ) || [],
    });
  }, [state.severityOptions, params.severity]);

  const styles = {
    placeholder: st => ({
      ...st,
      color: customTheme.colors.gray['400'],
    }),
  };

  return (
    <HStack>
      <Box zIndex={900}>
        <Select
          options={severityOptions}
          value={state.severity}
          onChange={s =>
            updateValue(
              'severity',
              map(s, o => o?.value?.toUpperCase()),
              key + camelcase(currentCategory as string) + '_severity',
            )
          }
          showIconInValueContainer
          isMulti
          showTotalSelected
          isAFilter
          styles={styles}
          placeholder="Severity"
          leftMessage="Severity"
          showUndefinedValueLabel={false}
        />
      </Box>
      <Box zIndex={900}>
        <Select
          options={state.resourceTypeOptions}
          value={state.resourceType}
          onChange={s =>
            updateValue(
              'resourceType',
              map(s, o => o.value),
              key + camelcase(currentCategory as string) + '_resource_type',
            )
          }
          isLoading={alertResourceTypes.isLoading}
          menuPortalTarget={document.body}
          menuShouldScrollIntoView
          isMulti
          showTotalSelected
          isAFilter
          styles={styles}
          leftMessage="Resource type"
          placeholder="Resource type"
          selectedMessage={
            'resource type' + (state.resourceType?.length === 1 ? '' : 's')
          }
          showUndefinedValueLabel={false}
        />
      </Box>
    </HStack>
  );
};
